import { Directive, ElementRef, Renderer2, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';

@Directive({
  selector: '[ncBorderBeam]'
})
export class BorderBeamDirective implements OnInit, OnDestroy, OnChanges {
  @Input() ncSize: number = 50; // 光条大小 单位px
  @Input() ncDuration: number = 6000; // 动画时长，ms
  @Input() ncStopTime: number = 0; // 停止动画时间，ms，0表示不停止
  @Input() ncColorFrom: string = '#ffaa40'; // 渐变起始颜色
  @Input() ncColorTo: string = '#9c40ff'; // 渐变结束颜色
  private _reverse: boolean = false;// 是否反向动画
  @Input()
  set ncReverse(val: boolean | string) {
    this._reverse = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncReverse(): boolean {
    return this._reverse;
  }
  @Input() ncInitialOffset: number = 0; // 初始偏移位置 (0-100) 单位%
  @Input() ncBorderWidth: number = 1; // 边框宽度 单位px
  @Input() ncEnabled: boolean = true; // 是否启用动画
  @Output() ncEnabledChange = new EventEmitter<boolean>(); // 双向绑定输出事件

  private borderContainer!: HTMLElement;
  private beamElement!: HTMLElement;
  private readonly animationName: string;
  private stopTimer: any;
  private styleElement!: HTMLElement;
  private isInitialized = false;

  constructor(private el: ElementRef, private renderer: Renderer2) {
    // 为每个实例生成唯一的动画名称
    this.animationName = `ncBorderBeam_${Math.random().toString(36).substring(2, 11)}`;
  }

  ngOnInit() {
    this.createBorderBeamContainer();
    this.isInitialized = true;
    if (this.ncEnabled) {
      this.startAnimation();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.isInitialized && changes['ncEnabled']) {
      if (this.ncEnabled) {
        this.startAnimation();
      } else {
        this.stopAnimation();
      }
    }
  }

  ngOnDestroy() {
    if (this.stopTimer) {
      clearTimeout(this.stopTimer);
    }
  }

  private createBorderBeamContainer() {
    // 创建边框容器
    this.borderContainer = this.renderer.createElement('div');
    this.renderer.addClass(this.borderContainer, 'border-beam-container');

    // 设置边框容器样式
    this.renderer.setStyle(this.borderContainer, 'position', 'absolute');
    this.renderer.setStyle(this.borderContainer, 'inset', '0');
    this.renderer.setStyle(this.borderContainer, 'borderRadius', 'inherit');
    this.renderer.setStyle(this.borderContainer, 'pointerEvents', 'none');
    this.renderer.setStyle(this.borderContainer, 'border', `${this.ncBorderWidth}px solid transparent`);
    this.renderer.setStyle(this.borderContainer, 'maskClip', 'padding-box, border-box');
    this.renderer.setStyle(this.borderContainer, 'maskComposite', 'intersect');
    this.renderer.setStyle(this.borderContainer, 'maskImage', 'linear-gradient(transparent, transparent), linear-gradient(#000, #000)');

    // 创建光条元素
    this.beamElement = this.renderer.createElement('div');
    this.renderer.addClass(this.beamElement, 'border-beam');

    // 设置光条样式
    this.renderer.setStyle(this.beamElement, 'position', 'absolute');
    this.renderer.setStyle(this.beamElement, 'width', `${this.ncSize}px`);
    this.renderer.setStyle(this.beamElement, 'aspectRatio', '1');
    this.renderer.setStyle(this.beamElement, 'background', `linear-gradient(to left, ${this.ncColorFrom}, ${this.ncColorTo}, transparent)`);
    this.renderer.setStyle(this.beamElement, 'offsetPath', `rect(0 auto auto 0 round ${this.ncSize}px)`);
    // 初始状态下隐藏光条
    this.renderer.setStyle(this.beamElement, 'opacity', '0');

    const elementPosition = this.el.nativeElement.style.position;
    if (!elementPosition || elementPosition === 'static') {
      this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');
    }

    // 添加关键帧动画样式
    this.styleElement = this.renderer.createElement('style');
    const startOffset = this.ncReverse ? `${100 - this.ncInitialOffset}%` : `${this.ncInitialOffset}%`;
    const endOffset = this.ncReverse ? `${-this.ncInitialOffset}%` : `${100 + this.ncInitialOffset}%`;

    const keyframes = `
      @keyframes ${this.animationName} {
        0% {
          offset-distance: ${startOffset};
        }
        100% {
          offset-distance: ${endOffset};
        }
      }
    `;
    this.renderer.appendChild(this.styleElement, this.renderer.createText(keyframes));
    this.renderer.appendChild(this.el.nativeElement, this.styleElement);

    // 将光条添加到边框容器中
    this.renderer.appendChild(this.borderContainer, this.beamElement);
    // 将边框容器添加到宿主元素中
    this.renderer.appendChild(this.el.nativeElement, this.borderContainer);
  }

  private startAnimation() {
    if (!this.beamElement) return;

    // 显示光条元素
    this.renderer.setStyle(this.beamElement, 'opacity', '1');
    this.renderer.setStyle(this.beamElement, 'animation',
      `${this.animationName} ${this.ncDuration}ms linear infinite`);

    // 如果设置了停止时间，则在指定时间后标记需要停止
    if (this.ncStopTime > 0) {
      this.stopTimer = setTimeout(() => {
        this.stopAnimation();
        // 当因为 ncStopTime 停止动画时，更新 ncEnabled 值并发出事件
        this.ncEnabled = false;
        this.ncEnabledChange.emit(false);
      }, this.ncStopTime);
    }
  }

  private stopAnimation() {
    if (!this.beamElement) return;

    if (this.stopTimer) {
      clearTimeout(this.stopTimer);
      this.stopTimer = null;
    }

    this.renderer.setStyle(this.beamElement, 'animation', 'none');
    this.renderer.setStyle(this.beamElement, 'opacity', '0');
  }
}